import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import imageTagService, {
  type ImageTag,
  type ImageTagAssignment,
  type ImageTagColor,
} from '../../../services/api/imageTagService';
import { useToast } from '../../../contexts/ToastContext';
import { useConfirm } from '../../../components/tasky';

// 絞り込みは URL に残し、そのまま送れば相手も同じ一覧を開けるようにする
const FILTER_PARAM = 'tags';

const byName = (a: ImageTag, b: ImageTag) => a.name.localeCompare(b.name, 'ja');

const parseFilter = (value: string | null): number[] =>
  (value ?? '')
    .split(',')
    .map((part) => Number(part))
    .filter((id) => Number.isInteger(id) && id > 0);

export interface BannerTagsController {
  /** 組織に入っていない利用者にはタグを出さない */
  enabled: boolean;
  tags: ImageTag[];
  filterIds: number[];
  setFilterIds: (ids: number[]) => void;
  createTag: (name: string) => Promise<ImageTag | null>;
  updateTag: (tag: ImageTag, changes: { name?: string; color?: ImageTagColor }) => Promise<void>;
  deleteTag: (tag: ImageTag) => Promise<void>;
  /** 付け外しできたら true。失敗したときは知らせて false を返す */
  assign: (imageIds: number[], changes: { add?: number[]; remove?: number[] }) => Promise<boolean>;
}

interface Options {
  enabled: boolean;
  /** 付け外しの結果をバナーの一覧に反映する */
  onAssigned: (assignments: ImageTagAssignment[]) => void;
  onTagChanged: (tag: ImageTag) => void;
  onTagDeleted: (tagId: number) => void;
}

export function useBannerTags({ enabled, onAssigned, onTagChanged, onTagDeleted }: Options): BannerTagsController {
  const { showToast } = useToast();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tags, setTags] = useState<ImageTag[]>([]);
  const [loaded, setLoaded] = useState(false);

  const filterKey = searchParams.get(FILTER_PARAM) ?? '';
  const requestedIds = useMemo(() => parseFilter(filterKey), [filterKey]);
  // 消されたタグの id が URL に残っていても、存在するタグだけで絞り込む
  const filterIds = useMemo(
    () => (loaded ? requestedIds.filter((id) => tags.some((tag) => tag.id === id)) : requestedIds),
    [loaded, requestedIds, tags],
  );

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    imageTagService
      .list()
      .then((list) => {
        if (cancelled) return;
        setTags([...list].sort(byName));
        setLoaded(true);
      })
      .catch((error) => console.error('Failed to load image tags:', error));
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const setFilterIds = useCallback(
    (ids: number[]) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (ids.length > 0) {
            next.set(FILTER_PARAM, ids.join(','));
          } else {
            next.delete(FILTER_PARAM);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const upsertTag = useCallback((tag: ImageTag) => {
    setTags((prev) => [...prev.filter((existing) => existing.id !== tag.id), tag].sort(byName));
  }, []);

  const createTag = useCallback(
    async (name: string) => {
      try {
        const tag = await imageTagService.create(name);
        upsertTag(tag);
        return tag;
      } catch (error) {
        console.error('Failed to create image tag:', error);
        showToast('タグを作成できませんでした', 'error');
        return null;
      }
    },
    [showToast, upsertTag],
  );

  const updateTag = useCallback(
    async (tag: ImageTag, changes: { name?: string; color?: ImageTagColor }) => {
      try {
        const updated = await imageTagService.update(tag.id, changes);
        upsertTag(updated);
        onTagChanged(updated);
      } catch (error: any) {
        console.error('Failed to update image tag:', error);
        showToast(
          error?.response?.status === 422 ? '同じ名前のタグがあります' : 'タグを変更できませんでした',
          'error',
        );
      }
    },
    [onTagChanged, showToast, upsertTag],
  );

  const deleteTag = useCallback(
    async (tag: ImageTag) => {
      const ok = await confirm({
        title: `「${tag.name}」を削除しますか？`,
        description: '付いているバナーからも外れます。',
        tone: 'destructive',
        confirmText: '削除する',
      });
      if (!ok) return;
      try {
        await imageTagService.remove(tag.id);
        setTags((prev) => prev.filter((existing) => existing.id !== tag.id));
        onTagDeleted(tag.id);
        if (requestedIds.includes(tag.id)) {
          setFilterIds(requestedIds.filter((id) => id !== tag.id));
        }
      } catch (error) {
        console.error('Failed to delete image tag:', error);
        showToast('タグを削除できませんでした', 'error');
      }
    },
    [confirm, onTagDeleted, requestedIds, setFilterIds, showToast],
  );

  // 付け外しは1つずつ送る。続けて押して応答が逆の順に届くと、古いタグの並びで表示を戻してしまうため
  const assignQueueRef = useRef<Promise<boolean> | null>(null);
  const assign = useCallback(
    (imageIds: number[], changes: { add?: number[]; remove?: number[] }): Promise<boolean> => {
      if (imageIds.length === 0) return Promise.resolve(true);
      const send = async () => {
        try {
          onAssigned(await imageTagService.assign(imageIds, changes));
          return true;
        } catch (error) {
          console.error('Failed to assign image tags:', error);
          showToast('タグを更新できませんでした', 'error');
          return false;
        }
      };
      // 前の付け外しが終わっていればすぐ送り、終わっていなければその応答を待ってから送る
      const pending = assignQueueRef.current;
      const result = pending ? pending.then(send) : send();
      assignQueueRef.current = result;
      void result.then(() => {
        if (assignQueueRef.current === result) assignQueueRef.current = null;
      });
      return result;
    },
    [onAssigned, showToast],
  );

  return { enabled, tags, filterIds, setFilterIds, createTag, updateTag, deleteTag, assign };
}
